<template>
  <div class="bz-picker">
    <!-- favorites -->
    <div v-if="favorites.length" class="bz-fav-chips">
      <span class="bz-fav-label">
        <i class="material-icons" style="font-size:13px;line-height:1;">star</i>Favorites
      </span>
      <button v-for="f in favorites" :key="f.locnum + f.zip" class="bz-fav-chip" type="button"
        @click="$emit('select', { company: f, method: 'favorite' })">
        {{ f.label }}<code v-if="f.zip">{{ f.zip }}</code>
      </button>
    </div>

    <!-- search row: text + state scope -->
    <div class="bz-comprow">
      <div class="bz-search" style="flex:1;">
        <i class="material-icons" style="font-size:18px;line-height:1;color:var(--bzc-faint);">search</i>
        <input v-model="q" placeholder="Search companies by name or city…" aria-label="Search companies" />
        <button v-if="q" class="bz-clear" type="button" @click="q = ''">
          <i class="material-icons" style="font-size:15px;line-height:1;">close</i>
        </button>
      </div>
      <select class="bz-select" style="flex:none;width:130px;" v-model="stateScope" aria-label="State scope">
        <option value="auto">All states</option>
        <option v-for="s in states" :key="s.code" :value="s.code">{{ s.name }}</option>
      </select>
    </div>

    <div v-if="qReady && !searching" class="bz-count-line">
      {{ results.length }} compan{{ results.length === 1 ? 'y' : 'ies' }} match “{{ q.trim() }}”{{ stateScope !== 'auto' ? ' in ' + stateScope : '' }}
    </div>

    <div class="bz-picker-list short" role="listbox">
      <div v-if="!qReady" class="bz-empty">
        Type at least 2 characters — results appear as you type, with location details so you can
        tell same-named companies apart. Narrow by state if the name is common.
      </div>
      <div v-else-if="searching" class="bz-empty">Searching…</div>
      <template v-else-if="results.length">
        <div v-for="c in results" :key="c.locnum + '|' + c.zip" class="bz-row"
          :class="{ sel: selected && selected.locnum === c.locnum && selected.zip === c.zip }"
          @click="$emit('select', { company: c, method: 'search' })">
          <i class="material-icons" style="font-size:16px;line-height:1;color:var(--bzc-faint);flex:none;">storefront</i>
          <div class="bz-row-main">
            <span class="bz-row-name">{{ c.name || c.label }}</span>
            <span class="bz-crumb">{{ companyCrumb(c) }}</span>
          </div>
          <span v-if="c.employees" class="bz-avail-chip neutral">{{ c.employees }} employees</span>
          <button class="bz-star" :class="{ on: isFav(c) }" type="button" title="Favorite"
            @click.stop="$emit('toggle-fav', c)">
            <i class="material-icons" style="font-size:16px;line-height:1;">{{ isFav(c) ? 'star' : 'star_outline' }}</i>
          </button>
        </div>
      </template>
      <div v-else class="bz-empty">
        No companies match “{{ q.trim() }}”{{ stateScope !== 'auto' ? ' in ' + stateScope : '' }}.
        Try fewer letters{{ stateScope !== 'auto' ? ' or All states' : '' }} — or check the spelling.
      </div>
    </div>
  </div>
</template>

<script>
// Live company search against the same endpoint classic uses
// (POST /user/search_lists/companyProfiles). Row shape: {label, locnum, zip, industry?}.
import { localStates } from '@/assets/common/location';
import { searchCompanies } from './model';
import { trackCompanySearched } from './analytics';

export default {
  name: 'company-step',
  props: {
    selected: { type: Object, default: null },
    favorites: { type: Array, default: () => [] },
  },
  data() {
    // localStates: [{name, code}] with 2-letter codes matching the DA state column
    return { q: '', stateScope: 'auto', states: localStates, results: [], searching: false, timer: null };
  },
  computed: {
    qReady() {
      return this.q.trim().length >= 2;
    },
  },
  watch: {
    q() {
      this.debouncedSearch();
    },
    stateScope() {
      this.debouncedSearch();
    },
  },
  methods: {
    companyCrumb(c) {
      const place = [c.city, c.state].filter(Boolean).join(', ');
      const industry = c.industry && String(c.industry).toUpperCase() !== 'UNKNOWN' ? c.industry : null;
      return [c.street, place, industry].filter(Boolean).join(' · ') || ('ZIP ' + (c.zip || ''));
    },
    isFav(c) {
      return this.favorites.some((f) => f.locnum === c.locnum && f.zip === c.zip);
    },
    debouncedSearch() {
      clearTimeout(this.timer);
      if (!this.qReady) {
        this.results = [];
        this.searching = false;
        return;
      }
      this.searching = true;
      this.timer = setTimeout(this.run, 350);
    },
    async run() {
      const term = this.q.trim();
      try {
        const list = await searchCompanies(term, this.stateScope);
        if (this.q.trim() === term) {
          this.results = list;
          this.searching = false;
          trackCompanySearched(term.length, this.stateScope, list.length);
        }
      } catch (e) {
        if (this.q.trim() === term) {
          this.results = [];
          this.searching = false;
        }
      }
    },
  },
};
</script>
